import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@Components/shadcn/ui/dropdown-menu";
import { LogOut } from "lucide-react";
import {
  LPLogoWName,
  dmSent,
  Automations,
  DashboardActive,
  DashboardInactive,
  InboxActive,
  InboxInactive,
  ContactsActive,
  ContactsInactive,
  AutomationsActive,
  AutomationsInactive,
  SchedulerActive,
  SchedulerInactive,
  CommentsActive,
  CommentsInactive,
  SupportInactive,
  SettingsInactive,
  Check,
  SettingsActive,
  ReferralActive,
  ReferralInactive
} from '@assets/index';
import styles from './styles';
import { ChevronDown } from "lucide-react";
import { RootState, useAppDispatch, useAppSelector } from '@Store/store';
import { onLogout } from '@Utils/common';

const navigationItems = [
  // {
  //   to: '/dashboard',
  //   label: 'Dashboard',
  //   activeIcon: DashboardActive,
  //   inactiveIcon: DashboardInactive,
  //   end: true
  // },
  {
    to: '/dashboard/automations',
    label: 'Automations',
    activeIcon: AutomationsActive,
    inactiveIcon: AutomationsInactive
  },
  // {
  //   to: '/dashboard/inbox',
  //   label: 'Inbox',
  //   activeIcon: InboxActive,
  //   inactiveIcon: InboxInactive
  // },
  {
    to: '/dashboard/contacts',
    label: 'Contacts',
    activeIcon: ContactsActive,
    inactiveIcon: ContactsInactive
  },
  // {
  //   to: '/dashboard/schedule',
  //   label: 'Schedule Content',
  //   activeIcon: SchedulerActive,
  //   inactiveIcon: SchedulerInactive
  // },
  // {
  //   to: '/dashboard/comments',
  //   label: 'Manage Comments',
  //   activeIcon: CommentsActive,
  //   inactiveIcon: CommentsInactive
  // }
  {
    to: 'referral',
    label: 'Refer and Earn',
    activeIcon: ReferralActive,
    inactiveIcon: ReferralInactive,
    isNew: true
  },
  {
    to: 'settings',
    label: 'Settings',
    activeIcon: SettingsActive,
    inactiveIcon: SettingsInactive
  },
];

const NavigationDrawer: React.FC = () => {
  const { user } = useAppSelector((state: RootState) => state.profile);

  const navigate = useNavigate();
  const dispatch = useAppDispatch()

  const handleLogout = () => {
    // Add your logout logic here
    // For example: clear local storage, reset auth state, etc.
    onLogout(dispatch)
    navigate('/login');
  };

  return (
    <aside style={styles.container}>
      {/* Logo Section */}
      <div style={styles.logoSection}>
        <img src={LPLogoWName} alt="LinkPlease Logo" style={styles.logoImage} />
      </div>

      {/* Workspace Selector with Dropdown */}
      <div className="border rounded-[10px] my-2">
        <DropdownMenu>
          <DropdownMenuTrigger className="w-full focus:outline-none">
            <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
              <img
                src={user?.ig_ids[0]?.profile_picture_url}
                alt="Profile"
                className="w-10 h-10 rounded-full object-cover"
              />
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-gray-900">@{user?.ig_ids[0]?.username}</p>
                <p className="text-xs text-gray-500"></p>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="min-w-[220px] max-w-[280px]">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-red-600 cursor-pointer"
              onClick={handleLogout}
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Navigation Links */}
      <nav style={styles.navigation}>
        {navigationItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            // end={item.end}
            style={({ isActive }) => styles.navLink(isActive)}
          >
            {({ isActive }) => (
              <>
                <img
                  src={isActive ? item.activeIcon : item.inactiveIcon}
                  alt={item.label}
                  style={styles.navIcon}
                />
                {item.label}
                {item?.isNew && (
                  <span style={{
                    position: 'absolute',
                    right: 25,
                    backgroundColor: 'gold',
                    color: 'black',
                    fontSize: 10,
                    padding: '2px 4px',
                    borderRadius: 6,
                    fontWeight: 'bold'
                  }}>
                    NEW
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Usage Metrics */}
      <div style={styles.metricsContainer}>
        <div style={styles.metricItem}>
          <div style={styles.metricHeader}>
            <img src={dmSent} alt="DM Sent Icon" style={styles.metricIcon} width="30" />
            <p style={styles.metricText}>Unlimited DM per month</p>
          </div>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, ...styles.progressGradient }} />
          </div>
        </div>

        <div style={styles.metricItem}>
          <div style={styles.metricHeader}>
            <img src={Automations} alt="Automations Icon" width="30" />
            <p style={styles.metricText}>Unlimited Automation per month</p>
          </div>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, ...styles.progressGradient }} />
          </div>
        </div>
      </div>

      <button
        className="w-full bg-[#3535d5] text-white py-2 rounded-lg flex items-center justify-center gap-2 hover:bg-[#7373ff] transition-colors"
      >
        <img src={Check} className="w-5 h-5 brightness-0 invert" alt="Check" />
        Unlimited Free Plan
      </button>

      {/* Bottom Actions */}
      <div style={styles.bottomActions}>
        {/* <button 
          style={styles.secondaryButton}
          className={'hover:bg-[#E0D9FF] hover:text-[#ffffff]'}
        >
          <img src={SettingsInactive} alt="Settings" style={styles.buttonIcon} />
          <span  style={styles.buttonText}>Settings</span>
        </button> */}
        <button
          style={styles.secondaryButton}
          className={'hover:bg-[#E0D9FF] hover:text-[#ffffff]'}
          onClick={() => window.open('https://wa.link/9jjwc0', '_blank')}
        >
          <img className='border rounded-full border-black' src={SupportInactive} alt="Settings" style={styles.buttonIcon} />
          <span style={styles.buttonText}>Support/Feedback</span>
        </button>
      </div>
    </aside>
  );
};

export default NavigationDrawer;